import { fg } from '@atlaskit/platform-feature-flags/fg';
import { exponentialRetry } from '@atlassian/conversation-assistant-service/common/utils/exponential-retry';

import { StreamErrorException } from './error-classes/StreamErrorException';
import { isRetryableReconnectionError } from './isRetryableReconnectionError';
import type { ReconnectableStreamOptions } from './reconnectable-stream';

const MAX_RECONNECT_ATTEMPTS = 5;
const RETRY_BASE_DELAY_MS = 1000;
const RECONNECT_JITTER_MS = 500;
const POST_ONLINE_DELAY_MS = 2000;

/**
 * Wraps an async generator with transparent reconnection on client-side
 * streaming errors. The consumer iterates the returned generator exactly
 * like a raw one — reconnection is an implementation detail.
 *
 * On disconnect:
 * 1. Evaluates `canReconnect()` — if false, rethrows.
 * 2. Calls `onBeforeFirstReconnect()` once for one-time ID alignment.
 * 3. Acquires a new generator via `getReconnectGenerator(lastEventId)`
 *    with exponential backoff.
 * 4. Resumes yielding chunks from the new generator.
 *
 * Server-side errors (StreamErrorException) are never retried.
 */
export async function* createReconnectableStream<T>({
	initialGenerator,
	canReconnect,
	getReconnectGenerator,
	getEventId,
	onBeforeFirstReconnect,
	onReconnecting,
	onReconnected,
	onFirstChunkAfterReconnect,
	onStreamCompleted,
	onStreamFailed,
	signal,
	maxAttempts = MAX_RECONNECT_ATTEMPTS,
	shouldContinueRetrying,
}: ReconnectableStreamOptions<T>): AsyncGenerator<T> {
	let currentGenerator = initialGenerator;
	let lastEventId: string | undefined;
	let reconnectCount = 0;
	let reconnectionAttempted = false;
	let awaitingFirstChunkAfterReconnect = false;
	const reliableContracts = fg('rovo_ai_mate_reliability_contracts');
	let settled = false;

	try {
		while (true) {
			try {
				for await (const chunk of currentGenerator) {
					if (signal?.aborted) {
						return;
					}

					const eventId = getEventId(chunk);
					if (eventId) {
						lastEventId = eventId;
					}

					if (awaitingFirstChunkAfterReconnect) {
						awaitingFirstChunkAfterReconnect = false;
						onFirstChunkAfterReconnect?.();
					}

					yield chunk;
				}

				if (reconnectionAttempted) {
					if (reliableContracts && signal?.aborted) {
						return;
					}
					settled = true;
					onStreamCompleted?.();
				}
				return;
			} catch (error) {
				if (reliableContracts && settled) {
					throw error;
				}
				if (error instanceof StreamErrorException) {
					if (reconnectionAttempted && !reliableContracts) {
						onStreamFailed?.(error);
					}
					throw error;
				}

				if (
					!isRetryableReconnectionError(error) ||
					!canReconnect() ||
					reconnectCount >= maxAttempts ||
					signal?.aborted
				) {
					if (reconnectionAttempted && !reliableContracts) {
						onStreamFailed?.(error);
					}
					throw error;
				}

				if (shouldContinueRetrying && !shouldContinueRetrying()) {
					if (reconnectionAttempted && !reliableContracts) {
						onStreamFailed?.(error);
					}
					throw error;
				}

				if (!reconnectionAttempted) {
					onBeforeFirstReconnect?.();
					reconnectionAttempted = true;
				}

				reconnectCount++;
				onReconnecting?.(reconnectCount, maxAttempts, lastEventId);

				currentGenerator = await exponentialRetry({
					fn: async () => getReconnectGenerator(lastEventId),
					maxRetries: 2,
					delay: RETRY_BASE_DELAY_MS,
					jitter: RECONNECT_JITTER_MS,
					pauseWhileOffline: true,
					postOnlineDelay: POST_ONLINE_DELAY_MS,
					errorFilter: isRetryableReconnectionError,
					signal,
				});

				onReconnected?.();
				awaitingFirstChunkAfterReconnect = true;
			}
		}
	} catch (error) {
		// Includes failures acquiring a replacement generator, not only failures
		// while reading it. A started reconnect experience must settle exactly once.
		if (reliableContracts && reconnectionAttempted && !settled) {
			settled = true;
			onStreamFailed?.(error);
		}
		throw error;
	} finally {
		if (reliableContracts && reconnectionAttempted && !settled) {
			settled = true;
			onStreamFailed?.(signal?.reason ?? new DOMException('Stream consumer stopped', 'AbortError'));
		}
	}
}
